"use client";

import type { ApexOptions } from "apexcharts";
import dynamic from "next/dynamic";
import { useTheme } from "@/providers";
import { Text, View } from "@/components";

// Lazy load the chart to avoid SSR issues
const ReactApexChart = dynamic(() => import("react-apexcharts"), {
  ssr: false
});

const DEFAULT_COLORS = ["#5D69F4", "#00998B", "#FFC861", "#FF6161", "#8169D3"];

export interface BarProps {
  /** Card title shown above the chart */
  title: string;
  /** Category labels (one per bar) */
  categories: string[];
  /** The data series to display in the chart */
  series: { name: string; data: number[] }[];
  /** The colors for each data series */
  colors?: string[];
  /** Render horizontal bars (useful for funnels / long labels) */
  horizontal?: boolean;
  /** Chart height in pixels */
  height?: number;
  /** Optional ApexCharts configuration to merge with defaults */
  options?: ApexOptions;
}

/**
 * @component
 * @name Bar
 * @description A customizable bar chart card. Follows the same theme-aware
 * pattern as the Polar organism (lazy client-only ApexCharts, light/dark via
 * el ThemeProvider del template) with a plain title header instead of the demo dropdown.
 *
 * @example
 * ```tsx
 * <Bar
 *   title="Avance por módulo"
 *   categories={["Sin módulos", "1. Introducción", "2. Señales"]}
 *   series={[{ name: "Alumnos", data: [4, 10, 6] }]}
 *   horizontal
 * />
 * ```
 */
const Bar = ({
  title,
  categories,
  series,
  colors = DEFAULT_COLORS,
  horizontal = false,
  height = 350,
  options
}: BarProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const defaultOptions: ApexOptions = {
    chart: {
      type: "bar",
      background: "transparent",
      animations: { enabled: true },
      fontFamily: "inherit",
      toolbar: { show: false }
    },
    colors: colors,
    plotOptions: {
      bar: {
        horizontal,
        borderRadius: 4,
        barHeight: "60%",
        columnWidth: "55%"
      }
    },
    xaxis: {
      categories: categories,
      labels: {
        style: { colors: isDark ? "#c8d2e4" : "#475569", fontSize: "12px" }
      },
      axisBorder: { color: isDark ? "rgba(255,255,255,0.18)" : "#e2e8f0" },
      axisTicks: { color: isDark ? "rgba(255,255,255,0.18)" : "#e2e8f0" }
    },
    yaxis: {
      labels: {
        style: { colors: isDark ? "#c8d2e4" : "#475569", fontSize: "12px" }
      }
    },
    grid: {
      borderColor: isDark ? "rgba(255,255,255,0.18)" : "#e2e8f0",
      strokeDashArray: 4
    },
    legend: {
      show: series.length > 1,
      labels: { colors: isDark ? "#c8d2e4" : "#475569" }
    },
    theme: {
      mode: isDark ? "dark" : "light"
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: "11px", fontWeight: 600 }
    },
    tooltip: {
      theme: isDark ? "dark" : "light"
    }
  };

  return (
    <View className="w-full p-6 rounded-2xl bg-card border border-border shadow-sm">
      <View className="pb-4 mb-2 border-b border-dashed border-border">
        <Text className="text-base font-semibold text-foreground">{title}</Text>
      </View>
      <View aria-label={title}>
        <ReactApexChart
          options={options ? { ...defaultOptions, ...options } : defaultOptions}
          series={series}
          type="bar"
          height={height}
          width="100%"
        />
      </View>
    </View>
  );
};

export default Bar;
